/**
 * Client-side utility to call Firebase Functions for distance calculations
 * Using Firebase SDK's httpsCallable for better integration
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase_config";

// Cache configuration
const CACHE_KEY_PREFIX = "distance_cache_";
const CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Generate a cache key based on origin and destinations
 * @param {Object} origin - Origin coordinates {lat, lng}
 * @param {Array} destinations - Array of destination coordinates
 * @returns {string} Cache key
 */
const generateCacheKey = (origin, destinations) => {
  // Round coordinates to 3 decimal places (~100m precision) to improve cache hits
  const roundedOrigin = {
    lat: Math.round(origin.lat * 1000) / 1000,
    lng: Math.round(origin.lng * 1000) / 1000,
  };

  // Create a hash of destinations
  const destHash = destinations
    .map((d) => `${Math.round(d.lat * 1000)},${Math.round(d.lng * 1000)}`)
    .join("|");

  return `${CACHE_KEY_PREFIX}${roundedOrigin.lat}_${roundedOrigin.lng}_${destHash}`;
};

/**
 * Get cached distance results if available and not expired
 * @param {string} cacheKey - Cache key
 * @returns {Array|null} Cached results or null
 */
const getCachedDistances = (cacheKey) => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is expired
    if (now - timestamp > CACHE_EXPIRY_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    console.log(
      "✅ Using cached distance data (age: " +
        Math.round((now - timestamp) / 1000 / 60) +
        " minutes)"
    );
    return data;
  } catch (error) {
    console.error("Error reading cache:", error);
    return null;
  }
};

/**
 * Save distance results to cache
 * @param {string} cacheKey - Cache key
 * @param {Array} data - Distance results to cache
 */
const setCachedDistances = (cacheKey, data) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log("💾 Distance data cached successfully");
  } catch (error) {
    console.error("Error saving to cache:", error);
    // If localStorage is full, clear old cache entries
    if (error.name === "QuotaExceededError") {
      clearOldCacheEntries();
      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ data, timestamp: Date.now() })
        );
      } catch (retryError) {
        console.error("Failed to cache after cleanup:", retryError);
      }
    }
  }
};

/**
 * Clear old cache entries to free up space
 */
const clearOldCacheEntries = () => {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter((key) => key.startsWith(CACHE_KEY_PREFIX));

    // Remove all cache entries older than expiry time
    const now = Date.now();
    cacheKeys.forEach((key) => {
      try {
        const cached = JSON.parse(localStorage.getItem(key));
        if (now - cached.timestamp > CACHE_EXPIRY_MS) {
          localStorage.removeItem(key);
        }
      } catch (e) {
        localStorage.removeItem(key);
      }
    });

    console.log("🧹 Cleared old cache entries");
  } catch (error) {
    console.error("Error clearing cache:", error);
  }
};

/**
 * Calculate distances for multiple restaurants using Firebase Function
 * @param {Object} origin - Origin coordinates {lat, lng}
 * @param {Array} destinations - Array of destination coordinates [{lat, lng}, ...]
 * @returns {Promise<Array>} Array of distance results
 */
export const calculateRestaurantDistances = async (origin, destinations) => {
  try {
    // Generate cache key
    const cacheKey = generateCacheKey(origin, destinations);

    // Check cache first
    const cachedResults = getCachedDistances(cacheKey);
    if (cachedResults) {
      return cachedResults;
    }

    console.log(
      `🚀 Calling Firebase Function for ${destinations.length} restaurants...`
    );

    // Create callable function reference
    const calculateDistances = httpsCallable(
      functions,
      "calculateRestaurantDistances"
      // {
      //   timeout: 60000, // 60 second timeout
      // }
    );

    // Call the function
    const result = await calculateDistances({
      origin,
      destinations,
    });

    console.log("Firebase Function response:", result.data);

    if (result.data.success) {
      // Cache the results
      setCachedDistances(cacheKey, result.data.results);
      return result.data.results;
    } else {
      throw new Error(result.data.error || "Failed to calculate distances");
    }
  } catch (error) {
    console.error("Error calling Firebase Function:", error);
    throw error;
  }
};

/**
 * Calculate distance for a single restaurant using Firebase Function
 * @param {Object} origin - Origin coordinates {lat, lng}
 * @param {Object} destination - Destination coordinates {lat, lng}
 * @returns {Promise<Object>} Distance result
 */
export const calculateSingleDistance = async (origin, destination) => {
  try {
    // Generate cache key for single destination
    const cacheKey = generateCacheKey(origin, [destination]);

    // Check cache first
    const cachedResults = getCachedDistances(cacheKey);
    if (cachedResults && cachedResults.length > 0) {
      const cached = cachedResults[0];
      return {
        distance: cached.distance,
        distanceText: cached.distanceText,
        duration: cached.duration,
        durationText: cached.durationText,
      };
    }

    console.log("🚀 Calling Firebase Function for single restaurant...");

    // Create callable function reference
    const calculateDistance = httpsCallable(
      functions,
      "calculateSingleDistance",
      {
        timeout: 30000, // 30 second timeout
      }
    );

    // Call the function
    const result = await calculateDistance({
      origin,
      destination,
    });

    console.log("Firebase Function response:", result.data);

    if (result.data.success) {
      const distanceData = {
        distance: result.data.distance,
        distanceText: result.data.distanceText,
        duration: result.data.duration,
        durationText: result.data.durationText,
        status: "OK",
      };

      // Cache the result
      setCachedDistances(cacheKey, [distanceData]);

      return distanceData;
    } else {
      throw new Error(result.data.error || "Failed to calculate distance");
    }
  } catch (error) {
    console.error("Error calling Firebase Function:", error);
    throw error;
  }
};

/**
 * Clear all distance cache entries
 * Useful for debugging or when user wants fresh data
 */
export const clearDistanceCache = () => {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter((key) => key.startsWith(CACHE_KEY_PREFIX));
    cacheKeys.forEach((key) => localStorage.removeItem(key));
    console.log(`🧹 Cleared ${cacheKeys.length} distance cache entries`);
  } catch (error) {
    console.error("Error clearing distance cache:", error);
  }
};
